import {
    forwardRef,
    useCallback,
    useEffect,
    useRef,
    type CSSProperties,
    type FocusEvent,
    type KeyboardEvent,
    type MouseEvent,
    type PointerEvent,
    type ReactElement,
    type ReactNode,
} from "react";
import { animate, motion, useMotionValue, type AnimationPlaybackControls } from "framer-motion";
import { clsx } from "clsx";
import { motion as motionTokens } from "@tokens/index";
import { capturePointer, releasePointer, setPressed } from "./press";
import { Text } from "./Text";
import { useReducedMotionPreference } from "./useReducedMotionPreference";
import pressable from "./pressable.module.css";
import styles from "./Switch.module.css";

/**
 * Switch: an on/off control with `role="switch"`.
 *
 * Fully controlled. The track crossfades color over 200ms; the thumb travels
 * on a spring. While pressed the thumb widens ~15% (the iOS "squish") and
 * returns on release. Drag the thumb to toggle: it follows the pointer and
 * commits once it passes 40% of the travel, else it snaps back. Space
 * toggles from the keyboard. `busy` shows a spinner inside the thumb and
 * blocks input while keeping the tab stop.
 *
 * Label the control with `label` (renders a wrapping `<label>`), or with
 * `aria-label` / `aria-labelledby` when the label lives elsewhere.
 */

export type SwitchSize = "sm" | "md";

export interface SwitchProps {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    disabled?: boolean;
    /** Show a spinner in the thumb and block input. Keeps the tab stop. */
    busy?: boolean;
    size?: SwitchSize;
    /** Visible label. Renders in a `<label>` that wraps the control. */
    label?: ReactNode;
    "aria-label"?: string;
    "aria-labelledby"?: string;
    "aria-describedby"?: string;
    id?: string;
    className?: string;
}

interface Geometry {
    readonly track: number;
    readonly thumb: number;
    readonly pad: number;
}

const geometry: Record<SwitchSize, Geometry> = {
    sm: { track: 36, thumb: 16, pad: 2 },
    md: { track: 44, thumb: 22, pad: 2 },
};

const PRESSED_WIDEN = 1.15;
const DRAG_SLOP_PX = 3;
// Fraction of the travel a drag must cover before the value commits.
const COMMIT_THRESHOLD = 0.4;

interface DragState {
    pointerId: number;
    startClientX: number;
    startX: number;
    moved: boolean;
}

interface ThumbTarget {
    readonly x: number;
    readonly width: number;
}

// Travel is the distance the thumb's leading edge moves between off and on.
function travelOf(g: Geometry): number {
    return g.track - 2 * g.pad - g.thumb;
}

function thumbTarget(g: Geometry, checked: boolean, pressed: boolean): ThumbTarget {
    const width = pressed ? Math.round(g.thumb * PRESSED_WIDEN) : g.thumb;
    // When on, the thumb keeps its trailing edge and widens toward the start.
    const x = checked ? travelOf(g) - (width - g.thumb) : 0;
    return { x, width };
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
    {
        checked,
        onCheckedChange,
        disabled = false,
        busy = false,
        size = "md",
        label,
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        "aria-describedby": ariaDescribedBy,
        id,
        className,
    },
    ref,
): ReactElement {
    const g = geometry[size];
    const inert = disabled || busy;

    const x = useMotionValue(thumbTarget(g, checked, false).x);
    const width = useMotionValue(g.thumb);
    const xAnimation = useRef<AnimationPlaybackControls | null>(null);
    const widthAnimation = useRef<AnimationPlaybackControls | null>(null);

    const dragRef = useRef<DragState | null>(null);
    const pressedRef = useRef(false);
    const suppressClickRef = useRef(false);

    const reduced = useReducedMotionPreference();
    const reducedRef = useRef(reduced);
    reducedRef.current = reduced;
    const checkedRef = useRef(checked);
    checkedRef.current = checked;

    const settle = useCallback(
        (toChecked: boolean, pressed: boolean, animated: boolean): void => {
            const target = thumbTarget(g, toChecked, pressed);
            xAnimation.current?.stop();
            widthAnimation.current?.stop();
            if (!animated || reducedRef.current) {
                x.set(target.x);
                width.set(target.width);
                return;
            }
            xAnimation.current = animate(x, target.x, motionTokens.springs.thumb);
            widthAnimation.current = animate(width, target.width, motionTokens.springs.thumb);
        },
        [g, width, x],
    );

    useEffect(() => {
        if (dragRef.current === null) {
            settle(checked, pressedRef.current, true);
        }
    }, [checked, settle]);

    const setPressedState = (root: HTMLElement, pressed: boolean): void => {
        pressedRef.current = pressed;
        setPressed(root, pressed);
    };

    // ── Pointer: press widens, drag follows, release commits or snaps back.

    const handlePointerDown = (event: PointerEvent<HTMLButtonElement>): void => {
        if (inert || event.button !== 0) return;
        const root = event.currentTarget;
        dragRef.current = {
            pointerId: event.pointerId,
            startClientX: event.clientX,
            startX: x.get(),
            moved: false,
        };
        capturePointer(root, event.pointerId);
        setPressedState(root, true);
        settle(checkedRef.current, true, true);
    };

    const handlePointerMove = (event: PointerEvent<HTMLButtonElement>): void => {
        const drag = dragRef.current;
        if (drag === null || drag.pointerId !== event.pointerId) return;
        const dx = event.clientX - drag.startClientX;
        if (!drag.moved && Math.abs(dx) < DRAG_SLOP_PX) return;
        if (!drag.moved) {
            drag.moved = true;
            suppressClickRef.current = true;
            event.currentTarget.setAttribute("data-dragging", "");
        }
        xAnimation.current?.stop();
        const maxX = travelOf(g) + g.thumb - width.get();
        x.set(Math.min(Math.max(drag.startX + dx, 0), maxX));
    };

    const endDrag = (event: PointerEvent<HTMLButtonElement>, cancelled: boolean): void => {
        const drag = dragRef.current;
        if (drag === null || drag.pointerId !== event.pointerId) return;
        dragRef.current = null;
        const root = event.currentTarget;
        releasePointer(root, event.pointerId);
        root.removeAttribute("data-dragging");
        setPressedState(root, false);

        const current = checkedRef.current;
        if (!drag.moved) {
            // A tap. The click event that follows toggles the value; the
            // `checked` effect then settles the thumb.
            settle(current, false, true);
            return;
        }
        window.setTimeout(() => {
            suppressClickRef.current = false;
        }, 0);

        const range = travelOf(g) + g.thumb - width.get();
        const progress = range > 0 ? x.get() / range : current ? 1 : 0;
        const next = cancelled
            ? current
            : current
              ? progress > 1 - COMMIT_THRESHOLD
              : progress >= COMMIT_THRESHOLD;

        if (next === current) {
            settle(current, false, true);
            return;
        }
        onCheckedChange(next);
        // The `checked` effect animates an accepted change; if the owner
        // rejected it, return to the value that is still in force.
        requestAnimationFrame(() => {
            if (checkedRef.current !== next) settle(checkedRef.current, false, true);
        });
    };

    const handlePointerLeave = (event: PointerEvent<HTMLButtonElement>): void => {
        // Capture keeps a drag alive; only a non-captured hover-out lands here.
        if (dragRef.current === null && pressedRef.current) {
            setPressedState(event.currentTarget, false);
            settle(checkedRef.current, false, true);
        }
    };

    // ── Keyboard: Space squishes on keydown and toggles on keyup (native click).

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
        if (inert || event.key !== " " || event.repeat) return;
        setPressedState(event.currentTarget, true);
        settle(checkedRef.current, true, true);
    };

    const handleKeyUp = (event: KeyboardEvent<HTMLButtonElement>): void => {
        if (event.key !== " ") return;
        setPressedState(event.currentTarget, false);
        settle(checkedRef.current, false, true);
    };

    const handleBlur = (event: FocusEvent<HTMLButtonElement>): void => {
        if (pressedRef.current) {
            setPressedState(event.currentTarget, false);
            settle(checkedRef.current, false, true);
        }
    };

    const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
        if (suppressClickRef.current) return;
        if (inert) {
            event.preventDefault();
            return;
        }
        onCheckedChange(!checked);
    };

    const sizeVars = {
        "--sw-track-w": `${String(g.track)}px`,
        "--sw-track-h": `${String(g.thumb + 2 * g.pad)}px`,
        "--sw-thumb": `${String(g.thumb)}px`,
        "--sw-pad": `${String(g.pad)}px`,
    } as CSSProperties;

    const control = (
        <button
            ref={ref}
            id={id}
            type="button"
            role="switch"
            aria-checked={checked}
            aria-busy={busy || undefined}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            disabled={disabled}
            className={clsx(
                pressable.pressable,
                styles.root,
                label === undefined ? className : undefined,
            )}
            style={sizeVars}
            data-size={size}
            data-hit-area={size === "sm" ? "" : undefined}
            onClick={handleClick}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={(event) => endDrag(event, false)}
            onPointerCancel={(event) => endDrag(event, true)}
            onPointerLeave={handlePointerLeave}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onBlur={handleBlur}
        >
            <motion.span aria-hidden="true" className={styles.thumb} style={{ x, width }}>
                {busy && <span data-spinner="" className={styles.spinner} />}
            </motion.span>
        </button>
    );

    if (label === undefined) {
        return control;
    }

    return (
        <label className={clsx(styles.field, className)} data-disabled={disabled || undefined}>
            <Text role="body">{label}</Text>
            {control}
        </label>
    );
});
